<template>
  <div class="edit-announcement">
    <transition name="slide-y-transition" mode="out-in">
      <v-card class="relative" flat v-if="!editMode" key="notEdit">
        <v-card-title class="title pb-0">
          {{ announcement.title }}
        </v-card-title>
        <v-card-text>{{ announcement.messages }}</v-card-text>
        <v-card-actions>
          <v-spacer />
          <span class="caption grey--text text--lighten-1">
            Last updated:
            {{ announcement.updated_at | moment('Do MMMM YYYY') }}
          </span>
        </v-card-actions>
        <v-btn
          class="floating-edit"
          fab
          dark
          small
          color="primary"
          @click="editMode = true"
        >
          <v-icon>edit</v-icon>
        </v-btn>
      </v-card>
      <v-card v-else class="mb-4" key="edit">
        <v-card-actions class="px-3 pb-0 pt-3">
          <div class="title-caption">
            <h5 class="title">
              Edit Announcement
            </h5>
            <span class="caption grey--text text--lighten-1">
              (Last updated:
              {{ announcement.updated_at | moment('Do MMMM YYYY') }})
            </span>
          </div>
          <v-spacer />
          <v-btn @click="cancelItem"><v-icon left>close</v-icon>cancel</v-btn>
        </v-card-actions>
        <v-card-text class="pa-3">
          <v-text-field
            :error-messages="errors.collect('title')"
            data-vv-name="title"
            data-vv-as="title"
            v-validate="'required|max:255'"
            label="Title"
            v-model="announcementTitle"
          />
          <v-textarea
            :error-messages="errors.collect('announcement')"
            data-vv-name="announcement"
            data-vv-as="announcement"
            v-validate="'required|max:10000'"
            label="Announcement"
            v-model="announcementText"
          />
        </v-card-text>
        <v-card-actions class="pa-3">
          <v-spacer></v-spacer>
          <v-btn
            color="red darken-1"
            dark
            text
            @click="deleteDialog = true"
            :disabled="loading"
          >
            delete announcement
          </v-btn>
          <v-btn
            color="primary"
            text
            @click="save"
            :loading="loading"
            :disabled="loading"
            >update announcement</v-btn
          >
        </v-card-actions>
      </v-card>
    </transition>
    <v-dialog v-model="saveDialog" persistent width="500">
      <v-card>
        <v-toolbar flat>
          <v-toolbar-title>Delete Message</v-toolbar-title>
        </v-toolbar>
        <v-card-text>
          You made some changes. Do you want to save it?
        </v-card-text>
        <v-card-actions class="px-3 pt-0">
          <v-spacer></v-spacer>
          <v-btn text @click="cancelChanges" :disabled="loading">
            discard changes
          </v-btn>
          <v-btn
            color="primary"
            text
            @click="save"
            :loading="loading"
            :disabled="loading"
          >
            save and send
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
    <v-dialog v-model="deleteDialog" persistent width="500">
      <v-card>
        <v-toolbar flat>
          <v-toolbar-title>Delete Message</v-toolbar-title>
        </v-toolbar>
        <v-card-text>
          Are you sure you want to delete this message? Changes are permanent.
        </v-card-text>
        <v-card-actions class="px-3 pt-0">
          <v-spacer></v-spacer>
          <v-btn text @click="deleteDialog = false" :disabled="loading">
            cancel
          </v-btn>
          <v-btn
            text
            @click="deleteItem"
            :loading="loading"
            :disabled="loading"
            color="red darken-1"
            dark
          >
            confirm delete
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </div>
</template>

<script>
import { mapMutations } from 'vuex'
import Class from '@/modules/class/models/Class'

export default {
  name: 'EditMessage',
  $_veeValidate: {
    validator: 'new'
  },
  props: {
    announcement: {
      type: Object,
      require: true
    }
  },
  data() {
    return {
      editMode: false,
      announcementTitle: this.announcement.title,
      announcementText: this.announcement.messages,
      loading: false,
      saveDialog: false,
      deleteDialog: false
    }
  },
  mounted() {},
  computed: {},
  watch: {
    announcement(value) {
      this.announcementText = value.announcements
      this.announcementTitle = value.title
    }
  },
  methods: {
    ...mapMutations('snackbar', ['showSnackbar', 'showErrorSnackbar']),
    cancelChanges() {
      this.announcementText = this.announcement.messages
      this.announcementTitle = this.announcement.title
      this.editMode = false
      this.saveDialog = false
    },
    cancelItem() {
      if (
        this.announcementTitle !== this.announcement.title ||
        this.announcementText !== this.announcement.messages
      ) {
        this.saveDialog = true
      } else {
        this.editMode = false
      }
    },
    async deleteItem() {
      this.loading = true
      const payload = {
        custom_class_id: this.announcement.custom_class_id,
        module: 'teacher'
      }
      try {
        await Class.dispatch('deleteMessage', {
          messageId: this.announcement.id,
          payload: payload
        })
        this.showSnackbar('Announcement deleted')
      } catch (error) {
        this.loading = false
        if (error.status === 422) {
          this.showErrorSnackbar('There was an error processing your request')
          return
        }
        this.showErrorSnackbar('There was a server error. Contact support')
      }
      this.loading = false
      this.editMode = false
    },
    async save() {
      // validate form
      let result = await this.$validator.validateAll()
      if (!result) return // there are errors
      this.loading = true
      const payload = {
        title: this.announcementTitle,
        messages: this.announcementText,
        custom_class_id: this.announcement.custom_class_id,
        module: 'teacher'
      }
      try {
        await Class.dispatch('updateMessage', {
          payload: payload,
          messageId: this.announcement.id
        })
        this.showSnackbar('Announcement updated and sent to class')
      } catch (error) {
        this.loading = false
        if (error.status === 422) {
          this.showErrorSnackbar('There was an error processing your request')
          return
        }
        this.showErrorSnackbar('There was a server error. Contact support')
      }
      this.loading = false
      this.editMode = false
    }
  }
}
</script>

<style lang="scss">
.edit-announcement {
  .floating-edit {
    position: absolute;
    right: 5px;
    top: 5px;
  }
}
</style>
